import { ArrowRightIcon, CalendarDaysIcon, BuildingOfficeIcon, UserGroupIcon, MapPinIcon, CursorArrowRaysIcon } from '@heroicons/react/24/outline';
import AnimatedLogoCloud from './AnimatedLogoCloud.js';
import { useData } from '../context/DataContext.js';
import { useState, useEffect } from 'react';

export default function Landing({ onNavigate }) {
  const { allEvents, promoters, partners, venues } = useData();
  const [eventClicks, setEventClicks] = useState(0);
  const [savedEvents, setSavedEvents] = useState(0);
  
  // Fetch admin stats (event clicks and saved events)
  useEffect(() => {
    const fetchAdminStats = async () => {
      try {
        const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/adminstats');
        if (response.ok) {
          const data = await response.json();
          setEventClicks(data.eventClicks || 0);
          setSavedEvents(data.savedEvents || 0);
        }
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      }
    };
    fetchAdminStats();
  }, []);
  
  // Calculate statistics
  const upcomingEvents = allEvents?.filter(event => {
    const eventDate = new Date(event.StartDateTime);
    return eventDate >= new Date();
  }).length || 0;
  
  const activePromoters = promoters?.length || 0;
  const activePartners = partners?.filter(p => p.isPaid === 1)?.length || 0;
  const totalVenues = venues?.length || 0;
  
  const stats = [
    { 
      label: 'Partners', 
      value: activePartners, 
      icon: UserGroupIcon
    },
    { 
      label: 'Venues', 
      value: totalVenues, 
      icon: MapPinIcon
    },
    { 
      label: 'Promoters', 
      value: activePromoters, 
      icon: BuildingOfficeIcon
    },
    { 
      label: 'Upcoming Events', 
      value: upcomingEvents, 
      icon: CalendarDaysIcon
    },
    { 
      label: 'Events Saved', 
      value: savedEvents, 
      icon: CalendarDaysIcon
    },
    { 
      label: 'Event Clicks', 
      value: eventClicks, 
      icon: CursorArrowRaysIcon
    },
  ];
  return (
    <div className="relative isolate flex flex-col overflow-x-hidden w-full">
      {/* Main CTA Banner */}
      <div className="px-4 sm:px-6 lg:px-8 pt-2 sm:pt-2 w-full max-w-full">
        <div className="mx-auto max-w-7xl py-2 sm:py-4 w-full">
          <div className="bg-slate-900/50 rounded-3xl overflow-hidden w-full">
            <div className="px-4 py-8 sm:px-12 sm:py-16 lg:flex lg:items-center lg:gap-x-10 w-full flex-col lg:flex-row">
              <div className="lg:w-1/2 lg:flex lg:flex-col lg:justify-center w-full lg:order-2 min-w-0">
                <div className="text-center lg:text-left">
                  <div className="flex items-start justify-center lg:justify-start gap-3 sm:gap-4">
                    <img
                      src="/driftcal_512_white.png"
                      alt="DriftCal Logo"
                      className="h-20 sm:h-24 lg:h-28 xl:h-32 w-auto object-contain shrink-0 self-center"
                    />
                    <div className="flex flex-col">
                      <h1 className="text-3xl xs:text-4xl sm:text-5xl font-bold tracking-tight text-white lg:text-6xl break-words leading-[1.2]">
                        DriftCal
                      </h1>
                      <p className="text-sm xs:text-base sm:text-lg text-gray-300 mt-0.5 break-words leading-[1.5]">
                        by <a 
                          href="https://advsolutions.net.au" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-white hover:text-gray-300 underline"
                        >
                          ADV Solutions
                        </a>
                      </p>
                    </div>
                  </div>
                  <p className="mt-4 sm:mt-6 text-sm xs:text-base sm:text-lg leading-6 sm:leading-8 text-gray-300 break-words">
                    Your one-stop destination for drift events
                  </p>
                  <div className="mt-2 sm:mt-4 text-xs xs:text-sm sm:text-base text-gray-300">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <div className="w-2 h-2 rounded-full bg-white/30 shrink-0" />
                      <span className="break-words">Find coming events</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <div className="w-2 h-2 rounded-full bg-white/30 shrink-0" />
                      <span className="break-words">Plan your season</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <div className="w-2 h-2 rounded-full bg-white/30 shrink-0" />
                      <span className="break-words">Promoters: Manage events</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="w-2 h-2 rounded-full bg-white/30 shrink-0" />
                      <span className="break-words">Partners: Advertise your business</span>
                    </div>
                  </div>
                  <div className="mt-6 sm:mt-10">
                    <button
                      onClick={() => onNavigate('events')}
                      className="group inline-flex items-center rounded-full bg-white/10 px-4 sm:px-6 py-2 sm:py-3 text-xs xs:text-sm font-semibold text-white hover:bg-white/20 w-full sm:w-auto justify-center animate-stroke-glow border-2 border-white/30 hover:border-white/50 transition-all duration-300"
                    >
                      Get Started
                      <ArrowRightIcon 
                        className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" 
                        aria-hidden="true" 
                      />
                    </button>
                  </div>
                </div>
              </div>
              <div className="lg:w-1/2 flex flex-col justify-center shrink-0 w-full mt-6 lg:mt-0 lg:order-1">
                {/* Info Graphics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <div
                        key={index}
                        className="bg-slate-900/50 rounded-xl p-3 sm:p-4 border border-white/10 hover:border-white/20 hover:bg-slate-900/60 hover:scale-105 transition-all duration-300 animate-fade-in-up"
                        style={{
                          animationDelay: `${index * 0.1}s`,
                          animationFillMode: 'both',
                        }}
                      >
                        <div className="flex flex-col">
                          <div className="flex items-center justify-center gap-2 mb-1">
                            <div className="inline-flex p-1.5 sm:p-2 rounded-lg bg-white/10">
                              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                            </div>
                            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                              {stat.value.toLocaleString()}
                            </div>
                          </div>
                          <div className="text-xs sm:text-sm text-gray-300 leading-tight text-center">
                            {stat.label}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partner Logos Section */}
      <div className="text-center mt-2 sm:mt-2 w-full max-w-full">
        <p className="text-xs sm:text-sm font-semibold text-gray-300 mb-2 sm:mb-4 px-4">
          Supported by:
        </p>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full" style={{ overflowX: 'hidden' }}>
          <div className="h-12 sm:h-16 lg:h-20 bg-slate-900/30 rounded-lg w-full max-w-full relative" style={{ overflow: 'hidden' }}>
            <AnimatedLogoCloud />
          </div>
        </div>
        {/* Contact Section */}
        <div className="text-center mt-4 sm:mt-6 w-full max-w-full px-4">
          <p className="text-xs sm:text-sm text-gray-300 break-words">
            Need a simplified business solution?{' '}
            <a 
              href="mailto:contact@shopadv.net" 
              className="text-white hover:text-gray-300 underline break-all"
            >
              contact@shopadv.net
            </a>
          </p>
          <p className="mt-3 text-xs text-gray-400">
            <a href="/privacy" className="text-gray-300 hover:text-white underline">
              Privacy
            </a>
            <span className="mx-2">·</span>
            <a href="/termsofservice" className="text-gray-300 hover:text-white underline">
              Terms
            </a>
          </p>
        </div>
      </div>
    </div>
  );
} 